/* eslint-disable no-script-url */
/* global URLPattern */
import 'urlpattern-polyfill'

export default class URI {
  static isMatchUriInList(list, uri) {
    return list.some(({ hostname, pathname }) => {
      const pattern = new URLPattern({
        protocol: 'http{s}?',
        hostname,
        pathname,
      })

      return pattern.test(uri)
    })
  }

  /**
   * INSIGN 내부 이동 일 경우, redirect_uri를 인코딩
   * e.g. FSS-500 redirect_uri에 + 가 포함 될 경우 유지하기 위함
   *
   * @param {string} uri - 처리할 URI
   * @returns {string} - 조건에 맞을 경우 인코딩된 URI
   */
  static encodeRedirectUriIfInnerRedirection(uri = '') {
    try {
      // signin.nexon.com 내부 이동 아닐 경우 redirect_uri 인코딩 하지 않음
      const allowedHostPattern = /^(https?:\/\/)(dev-|test-|pre-)?signin\.nexon\.com/i
      const urlObj = new URL(uri)
      if (!allowedHostPattern.test(urlObj.origin)) return uri

      // redirect_uri가 없으면 그대로 반환
      const match = uri.match(/[?&]redirect_uri=(.*)/)
      if (!match) return uri

      const rawRedirectUri = decodeURIComponent(match[1])

      // 임시대응: 일정 때문에 메이플퍼즐만 적용
      const redirectUrlObj = new URL(rawRedirectUri)
      const maplePuzzleHosts = [
        'dev.mlab.nexon.com', // 개발 환경
        'mp-dev.maplestory.nexon.com', // 개발 환경
        'mp-stg.maplestory.nexon.com', // 스테이지 환경
        'mp-lite-stg.maplestory.nexon.com', // 스테이지 환경
        'maplepuzzle.nexon.com', // 라이브 환경
        'maplepuzzlelite.nexon.com', // 라이브 환경
        'openapi.sooplive.com',
        'dkjwcf0jnnch3.cloudfront.net', // dev-signin.nexon.com 의 CF 주소
      ]
      const allowedRedirectHostPattern = new RegExp(`^(https?:\/\/)(${
        maplePuzzleHosts.map((h) => h.replace(/\./g, '\\.')).join('|')
      })`, 'i')
      if (!allowedRedirectHostPattern.test(redirectUrlObj.origin)) {
        console.warn('[encodeRedirectUriIfInnerRedirection] Not an allowed redirect URI:', rawRedirectUri)
        return uri
      }
      // end of 임시대응

      // signin.nexon.com 내부 이동일 경우 redirect_uri 인코딩 (메이플퍼즐 포함)
      const encoded = encodeURIComponent(rawRedirectUri)
      const newUri = uri.replace(/([?&]redirect_uri=).*/, `$1${encoded}`)
      console.warn('encoded redirect_uri for inner redirect:', newUri)

      return newUri
    } catch (e) {
      console.warn('[encodeRedirectUriIfInnerRedirection] Error:', e)
      return uri
    }
  }

  static navigateToUrlWithoutHistory(url) {
    try {
      const { protocol } = new URL(url, window.location.origin)
      if (/^(javascript|data|vbscript):/i.test(protocol)) {
        console.error('[navigateToUrlWithoutHistory] Blocked dangerous URI:', url)
        return
      }
    } catch (e) {
      console.error('[navigateToUrlWithoutHistory] Failed to parse URI:', url)
      return
    }
    window.location.replace(url)
  }

  /**
   * URI 검증 및 처리를 위한 통합 함수
   * 복호화, 디코딩, 유효성 검사를 모두 처리함
   * @param {string} uri 처리할 URI
   * @param {Object} options 옵션
   * @param {boolean} options.decrypt URI 복호화 여부
   * @param {Array} corsList 허용된 도메인 목록
   * @return {Object} { isValid: boolean, uri: string, isDecoded: boolean, name: string, message: string }
   */
  static validateAndProcessUri(uri, { decrypt = true, corsList = [] } = {}) {
    // 결과 객체 초기화
    const result = {
      isValid: false,
      uri: '',
      isDecoded: false, // 디코딩 여부를 나타내는 속성 추가
      name: '',
      message: '',
    }

    // URI가 없는 경우
    if (!uri) {
      result.message = '[validateAndProcessUri] Empty URI'
      return result
    }

    try {
      // 복호화 단계 (필요한 경우)
      let processedUri = uri
      let wasDecrypted = false
      if (decrypt) {
        try {
          processedUri = atob(uri)
          wasDecrypted = true
        } catch (e) {
          // console.warn('[validateAndProcessUri] Failed to decrypt URI:', e)
          // 복호화 실패 시 원본 URI 사용 (후속 검증에서 처리)
        }
      }

      // 디코딩 단계
      let decodedUri
      if (wasDecrypted) {
        decodedUri = processedUri
      } else {
        try {
          decodedUri = decodeURIComponent(processedUri)
          result.isDecoded = true
        } catch (e) {
          console.warn('[validateAndProcessUri] Failed to decode URI:', e)
          result.message = 'Failed to decode URI'
          return result
        }
      }

      // 위험한 프로토콜 검사 (XSS 방지)
      if (decodedUri.toLowerCase().trim().startsWith('javascript:')) {
        console.error('[validateAndProcessUri] Blocked JavaScript URL:', decodedUri)
        result.message = 'Blocked JavaScript protocol'
        return result
      }

      if (decodedUri.toLowerCase().trim().startsWith('data:')) {
        console.error('[validateAndProcessUri] Blocked data: URL:', decodedUri)
        result.message = 'Blocked data: protocol'
        return result
      }

      // 커스텀 스킴 처리 (corsList에서 확인)
      const customSchemeMatch = decodedUri.match(/^([^:]+):/)
      if (customSchemeMatch) {
        const scheme = customSchemeMatch[1]
        const isAllowedCustomScheme = corsList.includes(scheme)

        if (isAllowedCustomScheme) {
          console.log(`[validateAndProcessUri] Allowed custom scheme detected: ${scheme}`)
          result.isValid = true
          result.uri = decodedUri
          return result
        }
      }

      // URL 형식 검증 (HTTP/HTTPS만)
      let url
      try {
        url = new URL(decodedUri)
      } catch (e) {
        console.error('[validateAndProcessUri] Invalid URL format:', decodedUri)
        result.message = 'Invalid URL format'
        return result
      }

      // 프로토콜 검증 (HTTP/HTTPS만)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        console.error('[validateAndProcessUri] Invalid protocol:', url.protocol)
        result.message = 'Invalid protocol'
        return result
      }

      // 허용된 도메인인지 확인
      const isValidDomain = corsList.some((domain) => {
        const pattern = new URLPattern({
          protocol: 'http{s}?',
          hostname: domain,
        })

        return pattern.test(decodedUri)
      })

      if (!isValidDomain) {
        console.error(`[validateAndProcessUri] ${decodedUri} is not in the allowlist.`)
        result.name = 'NOT_IN_ALLOWLIST'
        result.message = 'Domain not in allowlist'
        result.uri = decodedUri
        return result
      }

      // 모든 검증을 통과한 경우
      result.isValid = true
      result.uri = decodedUri
      return result
    } catch (e) {
      // 예상치 못한 오류 발생 시
      console.error('[validateAndProcessUri] Unexpected error:', e)
      result.message = 'Unexpected error during URI validation'
      return result
    }
  }
}
